import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Crown, Trophy, Sparkles, Gem, X } from 'lucide-react'
import confetti from 'canvas-confetti'
import { toast } from 'react-hot-toast'
import audioService from '../../../services/audioService'
import { motion, AnimatePresence } from 'motion/react'
import { hapticNotify } from '../../../utils/haptic'

const BattleWinner = ({
  winner,
  totalPrize,
  isCurrentUser = false,
  isParticipant = false,
  skipEffects = false,
  primaryActionLabel,
  secondaryActionLabel,
  onPrimaryAction,
  onSecondaryAction,
  onClose
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(!skipEffects)

  useEffect(() => {
    if (skipEffects) {
      setIsOpen(false)
    }
  }, [skipEffects])

  const primaryLabel = primaryActionLabel || t('battle.newBattle')
  const secondaryLabel = secondaryActionLabel || t('battle.toBattles')

  const handlePrimaryAction = () => {
    if (typeof onPrimaryAction === 'function') {
      onPrimaryAction()
      return
    }
    setIsOpen(false)
    if (typeof onClose === 'function') onClose()
  }

  const handleSecondaryAction = () => {
    if (typeof onSecondaryAction === 'function') {
      onSecondaryAction()
      return
    }
    setIsOpen(false)
    if (typeof onClose === 'function') onClose()
  }

  useEffect(() => {
    if (!isOpen) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  useEffect(() => {
    // Если skipEffects = true (статус finished), пропускаем все эффекты (звуки и конфетти)
    if (skipEffects) {
      return
    }

    // Воспроизводим звук и показываем toast в зависимости от того, кто победил
    if (isCurrentUser) {
      // Если я победил - Win_Case_V1 и toast "Победа"
      audioService.play('caseBattleWin')
      hapticNotify('success')
      toast.success(t('battle.victory'), {
        duration: 3000,
        icon: '🏆'
      })
    } else if (isParticipant) {
      // Если я участвовал, но проиграл - Lost_Case_V1 и toast "Проиграл"
      audioService.play('caseBattleLost')
      hapticNotify('warning')
      toast.error(t('battle.lost'), {
        duration: 3000
      })
    } else {
      // Если я не участвовал в баттле - upgraderWin
      audioService.play('upgraderWin')
      hapticNotify('success')
    }

    // Показываем конфетти только если я победил
    if (isCurrentUser) {
      const duration = 3000
      const animationEnd = Date.now() + duration
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 }

      const randomInRange = (min, max) => Math.random() * (max - min) + min

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now()

        if (timeLeft <= 0) {
          clearInterval(interval)
          return
        }

        const particleCount = 50 * (timeLeft / duration)

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#00FF7F', '#4EFFC6', '#FFD700']
        })
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#00FF7F', '#4EFFC6', '#FFD700']
        })
      }, 250)

      return () => clearInterval(interval)
    }
  }, [isCurrentUser, isParticipant, skipEffects, t])

  const renderInlineWinner = () => (
    <div className="mb-6">
      <h2 className="text-xl font-bold text-white mb-4">{t('battle.help.step4Title')}</h2>

      <div className="relative bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary rounded-lg p-6 mb-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
        />

        <div className="absolute inset-0 bg-dark-primary/80" />

        <div className="relative flex flex-col items-center">
          <Crown className="w-16 h-16 text-yellow-400 mb-3 animate-pulse" />

          <img
            src={winner.user_ava || `https://ui-avatars.com/api/?name=${winner.user_name}&background=00FF7F&color=0A0A0F`}
            alt={winner.user_name}
            className="w-20 h-20 rounded-full border-4 border-accent-primary mb-3"
          />

          <p className="text-xl font-bold text-white mb-4">{winner.user_name}</p>

          <div className="bg-dark-primary rounded-lg p-4 w-full">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-text-secondary text-sm">{t('battle.totalPrize')}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-3xl font-bold text-accent-primary">
              <Gem className="w-8 h-8" />
              <span>{totalPrize?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {winner.items && winner.items.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {winner.items.map((item, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg p-3 border border-accent-primary/30 flex flex-col items-center text-center"
            >
              <img
                src={item.img}
                alt={item.name}
                className="w-32 h-32 aspect-square object-contain mb-2 mx-auto"
              />
              <p className="text-white text-xs font-medium text-center mb-1 w-full">{item.name}</p>
              <div className="flex items-center justify-center gap-1 text-accent-primary font-bold text-sm">
                <Gem className="w-4 h-4" />
                <span>{item.price?.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  if (skipEffects) {
    return renderInlineWinner()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] overflow-hidden bg-dark-primary" 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0 bg-dark-primary" />

          <div className="absolute inset-0">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-30"
              style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-primary/40 via-dark-primary/70 to-dark-primary" />
            <div className="absolute -top-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-accent-primary/20 blur-3xl" />
            <div className="absolute -bottom-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-accent-secondary/20 blur-3xl" />
          </div>

          <div
            className="absolute inset-0 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="relative min-h-full w-full">
            <button
              onClick={() => {
                setIsOpen(false)
                if (typeof onClose === 'function') onClose()
              }}
              className="fixed right-4 top-4 z-10 h-10 w-10 rounded-full bg-surface/60 text-white hover:bg-surface/80 transition-colors flex items-center justify-center"
              aria-label="Закрыть"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="w-full px-4 pt-16 pb-32 flex items-start justify-center">
              <motion.div
                className="relative w-full max-w-[620px]"
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ duration: 0.28, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  {isCurrentUser ? (
                    <>
                      <Trophy className="w-6 h-6 text-yellow-400" />
                      <h2 className="text-2xl font-bold text-white">{t('battle.victory')}</h2>
                      <Sparkles className="w-6 h-6 text-yellow-400" />
                    </>
                  ) : (
                    <>
                      <Crown className="w-6 h-6 text-accent-primary" />
                      <h2 className="text-2xl font-bold text-white">{t('battle.help.step4Title')}</h2>
                      <Crown className="w-6 h-6 text-accent-primary" />
                    </>
                  )}
                </div>

                <div className="relative bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary rounded-2xl p-5 overflow-hidden shadow-2xl shadow-accent-primary/20">
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
                  />

                  <div className="absolute inset-0 bg-dark-primary/80" />

                  <div className="relative">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center mb-2">
                        <Crown className="w-14 h-14 text-yellow-400 animate-pulse" />
                      </div>

                      <img
                        src={winner.user_ava || `https://ui-avatars.com/api/?name=${winner.user_name}&background=00FF7F&color=0A0A0F`}
                        alt={winner.user_name}
                        className="w-24 h-24 rounded-full border-4 border-accent-primary shadow-lg shadow-accent-primary/30"
                      />

                      <p className="text-2xl font-bold text-white mt-3 mb-4 text-center">
                        {winner.user_name}
                      </p>

                      <div className="bg-dark-primary/80 backdrop-blur-sm rounded-xl p-4 w-full border border-white/10">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <p className="text-text-secondary text-sm">{t('battle.totalPrize')}</p>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-3xl font-bold text-accent-primary">
                          <Gem className="w-8 h-8" />
                          <span>{totalPrize?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {winner.items && winner.items.length > 0 && (
                      <div className="mt-4 max-h-[38vh] overflow-auto">
                        <div className="grid grid-cols-2 gap-3">
                          {winner.items.map((item, index) => (
                            <div
                              key={index}
                              className="bg-surface/70 backdrop-blur-sm rounded-xl p-3 border border-accent-primary/30 flex flex-col items-center text-center"
                            >
                              <img
                                src={item.img}
                                alt={item.name}
                                className="w-32 h-32 aspect-square object-contain mb-2 mx-auto"
                              />
                              <p className="text-white text-xs font-medium text-center mb-1 w-full">{item.name}</p>
                              <div className="flex items-center justify-center gap-1 text-accent-primary font-bold text-sm">
                                <Gem className="w-4 h-4" />
                                <span>{item.price?.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-10 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="mx-auto w-full max-w-[620px]">
                <div className="rounded-2xl border border-white/10 bg-dark-primary/95 backdrop-blur-md p-3 shadow-2xl shadow-black/40">
                  <div className="flex gap-3">
                    <button
                      onClick={handlePrimaryAction}
                      className="flex-1 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-xl font-semibold hover:opacity-90 transition-opacity"
                    >
                      {primaryLabel}
                    </button>
                    <button
                      onClick={handleSecondaryAction}
                      className="flex-1 py-3 bg-surface/70 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-surface/90 transition-colors border border-white/20"
                    >
                      {secondaryLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </motion.div>
      )}
      {!isOpen && renderInlineWinner()}
    </AnimatePresence>
  )
}

export default BattleWinner
